import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { campoApi, referenciaApi, configuracaoApi } from '../services/api';
import { PageHeader, Loading, EmptyState, Modal } from '../components/ui';
import {
  SlidersHorizontal, Plus, Trash2, Pencil, Check, X,
  ListChecks, ToggleLeft, Tag, Wallet,
} from 'lucide-react';

const tiposCampo = [
  { value: 'SELECAO', label: 'Seleção (lista de opções)' },
  { value: 'TEXTO', label: 'Texto livre' },
  { value: 'CHECKBOX', label: 'Checkbox (sim/não)' },
  { value: 'NUMERO', label: 'Número' },
  { value: 'DATA', label: 'Data' },
];

const tipoCampoLabel = (t: string) => tiposCampo.find(x => x.value === t)?.label || t;

interface Campo {
  id: string;
  nome: string;
  label: string;
  tipoCampo: string;
  tipoReferencia?: string | null;
  obrigatorio: boolean;
  ativo: boolean;
  ordem: number;
  padrao: boolean;
}

export function ItensPadraoPage() {
  const queryClient = useQueryClient();
  const [modalCampo, setModalCampo] = useState(false);
  const [campoEdicao, setCampoEdicao] = useState<Campo | null>(null);

  const { data: campos, isLoading } = useQuery({
    queryKey: ['campos-pedido'],
    queryFn: () => campoApi.listar().then(r => r.data as Campo[]),
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['campos-pedido'] });

  const remover = useMutation({
    mutationFn: (id: string) => campoApi.remover(id),
    onSuccess: invalidar,
  });

  const alternar = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) => campoApi.atualizar(id, dados),
    onSuccess: invalidar,
  });

  const camposSelecao = (campos || []).filter(c => c.tipoCampo === 'SELECAO' && c.ativo);

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Itens Padrão"
        descricao="Configure os campos do formulário de pedidos e as opções de cada lista. Mudanças valem para os próximos pedidos — pedidos já criados não são alterados."
      >
        <button onClick={() => setModalCampo(true)} className="btn-primary">
          <Plus className="w-4 h-4" />
          Novo Campo
        </button>
      </PageHeader>

      {isLoading ? (
        <Loading altura="h-64" />
      ) : (
        <>
          {/* ── Campos do pedido ─────────────────────────── */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center">
                  <SlidersHorizontal className="w-4 h-4 text-brand-600" />
                </span>
                Campos do Pedido
              </h2>
            </div>
            <div className="space-y-2.5">
              {(campos || []).map(campo => (
                <div
                  key={campo.id}
                  className={`flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl ring-1 ring-inset transition-colors ${
                    campo.ativo ? 'bg-white ring-ink-200/70' : 'bg-ink-50/60 ring-ink-200/40 opacity-60'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-ink-900 flex items-center gap-2">
                      {campo.label}
                      {campo.padrao && (
                        <span className="badge bg-ink-100 text-ink-500 ring-1 ring-inset ring-ink-400/20">padrão</span>
                      )}
                      {campo.obrigatorio && (
                        <span className="badge bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20">obrigatório</span>
                      )}
                    </p>
                    <p className="text-xs text-ink-400 mt-0.5">{tipoCampoLabel(campo.tipoCampo)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => alternar.mutate({ id: campo.id, dados: { obrigatorio: !campo.obrigatorio } })}
                      className="btn-secondary !py-1.5 !px-3 text-2xs"
                      title="Alternar obrigatório"
                    >
                      {campo.obrigatorio ? 'Tornar opcional' : 'Tornar obrigatório'}
                    </button>
                    <button
                      onClick={() => setCampoEdicao(campo)}
                      className="icon-btn"
                      title="Editar campo"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => alternar.mutate({ id: campo.id, dados: { ativo: !campo.ativo } })}
                      className={`icon-btn ${campo.ativo ? '' : '!text-emerald-600 hover:!bg-emerald-50'}`}
                      title={campo.ativo ? 'Desativar campo' : 'Reativar campo'}
                    >
                      <ToggleLeft className="w-4 h-4" />
                    </button>
                    {!campo.padrao && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Remover o campo "${campo.label}"? Pedidos existentes não são afetados.`)) {
                            remover.mutate(campo.id);
                          }
                        }}
                        className="icon-btn hover:!bg-red-50 hover:!text-red-600"
                        title="Remover campo"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Opções das listas de seleção ─────────────── */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <ListChecks className="w-4 h-4 text-blue-600" />
                </span>
                Opções das Listas de Seleção
              </h2>
            </div>
            {camposSelecao.length === 0 ? (
              <EmptyState
                icon={ListChecks}
                titulo="Nenhum campo de seleção ativo"
                descricao="Campos do tipo seleção aparecem aqui para você gerenciar as opções."
              />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {camposSelecao.map(campo => (
                  <OpcoesCampo key={campo.id} campo={campo} />
                ))}
              </div>
            )}
          </div>

          {/* ── Categorias financeiras (custo x despesa) ──── */}
          <CategoriasCusto />
        </>
      )}

      {/* Modal: novo campo */}
      <ModalNovoCampo aberto={modalCampo} onFechar={() => setModalCampo(false)} />

      {/* Modal: editar campo */}
      {campoEdicao && (
        <ModalEditarCampo campo={campoEdicao} onFechar={() => setCampoEdicao(null)} />
      )}
    </div>
  );
}

/* ── Opções de um campo de seleção ─────────────────────── */
function OpcoesCampo({ campo }: { campo: Campo }) {
  const queryClient = useQueryClient();
  const [nova, setNova] = useState('');
  const [editando, setEditando] = useState<string | null>(null);
  const [valorEdicao, setValorEdicao] = useState('');

  const { data: opcoes } = useQuery({
    queryKey: ['referencias', campo.tipoReferencia],
    queryFn: () =>
      referenciaApi.listar({ tipo: campo.tipoReferencia, limit: 200 }).then(r => r.data),
    enabled: !!campo.tipoReferencia,
  });

  const invalidar = () =>
    queryClient.invalidateQueries({ queryKey: ['referencias', campo.tipoReferencia] });

  const adicionar = useMutation({
    mutationFn: () => referenciaApi.criar({ tipo: campo.tipoReferencia, valor: nova.trim() }),
    onSuccess: () => { setNova(''); invalidar(); },
  });

  const renomear = useMutation({
    mutationFn: ({ id, valor }: { id: string; valor: string }) => referenciaApi.atualizar(id, valor),
    onSuccess: () => { setEditando(null); invalidar(); },
  });

  const remover = useMutation({
    mutationFn: (id: string) => referenciaApi.remover(id),
    onSuccess: invalidar,
  });

  return (
    <div className="rounded-xl ring-1 ring-inset ring-ink-200/70 p-4">
      <p className="text-sm font-semibold text-ink-800 mb-3 flex items-center gap-2">
        <Tag className="w-3.5 h-3.5 text-ink-400" />
        {campo.label}
      </p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {(opcoes || []).map((o: any) => (
          <span
            key={o.id}
            className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-lg bg-ink-100 text-xs font-medium text-ink-700"
          >
            {editando === o.id ? (
              <>
                <input
                  value={valorEdicao}
                  onChange={e => setValorEdicao(e.target.value)}
                  className="w-24 px-1 py-0.5 text-xs rounded border border-ink-300"
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'Enter' && valorEdicao.trim()) renomear.mutate({ id: o.id, valor: valorEdicao.trim() });
                    if (e.key === 'Escape') setEditando(null);
                  }}
                />
                <button onClick={() => valorEdicao.trim() && renomear.mutate({ id: o.id, valor: valorEdicao.trim() })} className="p-0.5 text-emerald-600 hover:bg-emerald-100 rounded">
                  <Check className="w-3 h-3" />
                </button>
                <button onClick={() => setEditando(null)} className="p-0.5 text-ink-400 hover:bg-ink-200 rounded">
                  <X className="w-3 h-3" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => { setEditando(o.id); setValorEdicao(o.valor); }}
                  className="hover:text-brand-700"
                  title="Renomear"
                >
                  {o.valor}
                </button>
                <button
                  onClick={() => remover.mutate(o.id)}
                  className="p-0.5 text-ink-400 hover:text-red-600 hover:bg-red-50 rounded"
                  title="Remover opção"
                >
                  <X className="w-3 h-3" />
                </button>
              </>
            )}
          </span>
        ))}
        {(opcoes || []).length === 0 && (
          <span className="text-xs text-ink-400 italic">Nenhuma opção cadastrada.</span>
        )}
      </div>
      <div className="flex gap-2">
        <input
          value={nova}
          onChange={e => setNova(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && nova.trim()) adicionar.mutate(); }}
          placeholder={`Nova opção de ${campo.label.toLowerCase()}...`}
          className="input-field !py-1.5 text-sm flex-1"
        />
        <button
          onClick={() => nova.trim() && adicionar.mutate()}
          disabled={!nova.trim() || adicionar.isPending}
          className="btn-secondary !py-1.5 !px-3 text-[13px]"
        >
          <Plus className="w-3.5 h-3.5" /> Adicionar
        </button>
      </div>
    </div>
  );
}

/* ── Categorias de custo (projeção de caixa) ───────────── */
function CategoriasCusto() {
  const queryClient = useQueryClient();

  const { data: categorias } = useQuery({
    queryKey: ['referencias', 'CATEGORIA_FINANCEIRA'],
    queryFn: () => referenciaApi.listar({ tipo: 'CATEGORIA_FINANCEIRA', limit: 200 }).then(r => r.data),
  });

  const { data: config } = useQuery({
    queryKey: ['config', 'categorias_custo'],
    queryFn: () => configuracaoApi.ler('categorias_custo').then(r => r.data),
  });

  const custosPadrao = ['Tecido/Malha', 'Aviamentos', 'Costura', 'DTF', 'Serigrafia', 'Bordado', 'Frete'];
  const selecionadas: string[] = config?.valor ?? custosPadrao;

  const salvar = useMutation({
    mutationFn: (valor: string[]) =>
      configuracaoApi.salvar('categorias_custo', valor, 'Categorias de saída consideradas custo na projeção de caixa'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['config', 'categorias_custo'] }),
  });

  const alternarCategoria = (valor: string) => {
    const nova = selecionadas.includes(valor)
      ? selecionadas.filter(c => c !== valor)
      : [...selecionadas, valor];
    salvar.mutate(nova);
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
            <Wallet className="w-4 h-4 text-red-600" />
          </span>
          Categorias de Custo
        </h2>
      </div>
      <p className="text-[13px] text-ink-500 mb-4">
        Na projeção de caixa do dashboard, saídas dessas categorias aparecem como <strong>custos</strong>;
        as demais saídas aparecem como <strong>despesas</strong>.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {(categorias || []).map((c: any) => {
          const ativo = selecionadas.includes(c.valor);
          return (
            <button
              key={c.id}
              onClick={() => alternarCategoria(c.valor)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                ativo
                  ? 'bg-red-600 text-white border-red-600 shadow-sm'
                  : 'bg-white text-ink-500 border-ink-200 hover:border-ink-300'
              }`}
            >
              {c.valor}
            </button>
          );
        })}
        {(categorias || []).length === 0 && (
          <span className="text-xs text-ink-400 italic">
            Nenhuma categoria financeira cadastrada ainda — elas surgem conforme você lança no financeiro.
          </span>
        )}
      </div>
    </div>
  );
}

/* ── Modal: novo campo personalizado ───────────────────── */
function ModalNovoCampo({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  const [tipoCampo, setTipoCampo] = useState('SELECAO');
  const [obrigatorio, setObrigatorio] = useState(false);

  const criar = useMutation({
    mutationFn: () => campoApi.criar({ label: label.trim(), tipoCampo, obrigatorio }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campos-pedido'] });
      setLabel('');
      setTipoCampo('SELECAO');
      setObrigatorio(false);
      onFechar();
    },
  });

  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      titulo="Novo Campo Personalizado"
      descricao="O campo passa a aparecer no formulário de novos pedidos."
    >
      <form
        onSubmit={e => { e.preventDefault(); if (label.trim()) criar.mutate(); }}
        className="space-y-4"
      >
        <div>
          <label className="input-label">Nome do campo *</label>
          <input
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="Ex: Gola, Punho, Estampa nas costas..."
            className="input-field"
            required
            autoFocus
          />
        </div>
        <div>
          <label className="input-label">Tipo de campo</label>
          <select value={tipoCampo} onChange={e => setTipoCampo(e.target.value)} className="input-field">
            {tiposCampo.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          {tipoCampo === 'SELECAO' && (
            <p className="text-2xs text-ink-400 mt-1.5">
              Após criar, cadastre as opções na seção "Opções das Listas de Seleção".
            </p>
          )}
        </div>
        <label className="flex items-center gap-2.5 text-sm text-ink-700 cursor-pointer">
          <input
            type="checkbox"
            checked={obrigatorio}
            onChange={e => setObrigatorio(e.target.checked)}
            className="w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
          />
          Campo obrigatório no pedido
        </label>
        <div className="flex justify-end gap-3 pt-1">
          <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
          <button type="submit" disabled={criar.isPending || !label.trim()} className="btn-primary">
            {criar.isPending ? 'Criando...' : 'Criar Campo'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ── Modal: editar campo ───────────────────────────────── */
function ModalEditarCampo({ campo, onFechar }: { campo: Campo; onFechar: () => void }) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(campo.label);
  const [tipoCampo, setTipoCampo] = useState(campo.tipoCampo);
  const [obrigatorio, setObrigatorio] = useState(campo.obrigatorio);

  const salvar = useMutation({
    mutationFn: () => campoApi.atualizar(campo.id, { label: label.trim(), tipoCampo, obrigatorio }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campos-pedido'] });
      onFechar();
    },
  });

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo={`Editar "${campo.label}"`}
      descricao="A mudança altera o padrão para todos os próximos pedidos, sem afetar pedidos já criados ou em andamento."
    >
      <form
        onSubmit={e => { e.preventDefault(); if (label.trim()) salvar.mutate(); }}
        className="space-y-4"
      >
        <div>
          <label className="input-label">Nome do campo *</label>
          <input value={label} onChange={e => setLabel(e.target.value)} className="input-field" required />
        </div>
        <div>
          <label className="input-label">Tipo de campo</label>
          <select value={tipoCampo} onChange={e => setTipoCampo(e.target.value)} className="input-field">
            {tiposCampo.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          {campo.tipoCampo !== tipoCampo && (
            <p className="flex items-center gap-1.5 text-2xs text-amber-600 mt-1.5">
              Tipo alterado de {tipoCampoLabel(campo.tipoCampo)} para {tipoCampoLabel(tipoCampo)}.
            </p>
          )}
        </div>
        <label className="flex items-center gap-2.5 text-sm text-ink-700 cursor-pointer">
          <input
            type="checkbox"
            checked={obrigatorio}
            onChange={e => setObrigatorio(e.target.checked)}
            className="w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
          />
          Campo obrigatório no pedido
        </label>
        <div className="flex justify-end gap-3 pt-1">
          <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
          <button type="submit" disabled={salvar.isPending || !label.trim()} className="btn-primary">
            {salvar.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
